/**
 * Schema markup JSON-LD: business node (datos del host) + Service (dinámico).
 * Script aislado con `data-seo-host="jsonld"` para poder identificarlo/limpiarlo
 * sin afectar los otros bloques LD+JSON que ya inyecta el servidor.
 *
 * En un subdominio de marca el nodo es un Podiatrist con los datos de la clínica
 * (teléfono, dirección, área). En el apex de plataforma no hay clínica que
 * publicar: se emite `Organization` sin teléfono/dirección/geo, igual que hace
 * el layout del servidor.
 */
import {
  siteDomain,
  siteName,
  serviceArea,
  bookingUrl,
  isoDuration,
  durationMinutes,
  buildCanonicalUrl,
  type SeoService,
} from '@/services/seo/serviceSeo';
import { loadBranding } from '@/services/branding.service';

export function salonId(): string {
  return `https://${siteDomain()}/#podiatrist`;
}

export interface SalonSchema {
  '@type': 'Podiatrist' | 'Organization';
  '@id': string;
  name: string;
  url: string;
  logo: string;
  image: string;
  priceRange: string;
  telephone?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress: string;
    addressLocality: string;
    addressCountry: string;
  };
  geo?: { '@type': 'GeoCoordinates'; latitude: number; longitude: number };
  areaServed?: string;
  sameAs: string[];
  potentialAction: {
    '@type': 'ReserveAction';
    target: { '@type': 'EntryPoint'; urlTemplate: string; inLanguage: string };
    name: string;
  };
}

export interface ServiceOfferSchema {
  '@type': 'Offer';
  url: string;
  price: string;
  priceCurrency: 'CLP';
  availability: string;
}

export interface ServiceSchema {
  '@type': 'Service';
  name: string;
  serviceType: string;
  description: string;
  url: string;
  provider: { '@id': string };
  areaServed?: string;
  duration: string;
  offers?: ServiceOfferSchema;
}

export interface SeoGraph {
  '@context': 'https://schema.org';
  '@graph': [SalonSchema, ServiceSchema];
}

/** Nodo del negocio: clínica en subdominios de marca, organización en el apex. */
export function salonSchema(): SalonSchema {
  const branding = loadBranding();
  const domain = siteDomain();
  const address = branding.address;
  const [street, ...rest] = (address || '').split(',').map((part) => part.trim());

  return {
    '@type': branding.isPlatform ? 'Organization' : 'Podiatrist',
    '@id': salonId(),
    name: siteName(),
    url: `https://${domain}`,
    logo: `https://${domain}/images/logo.jpeg`,
    image: `https://${domain}/images/logo.jpeg`,
    priceRange: '$$',
    ...(branding.phone ? { telephone: branding.phone } : {}),
    ...(address
      ? {
          address: {
            '@type': 'PostalAddress',
            streetAddress: street,
            addressLocality: rest.join(', ') || serviceArea(),
            addressCountry: 'CL',
          },
        }
      : {}),
    ...(serviceArea() ? { areaServed: serviceArea() } : {}),
    sameAs: [],
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: bookingUrl(),
        inLanguage: 'es-CL',
      },
      name: `Reservar cita online en ${siteName()}`,
    },
  };
}

export function serviceSchema(service: SeoService): ServiceSchema {
  const canonicalUrl = buildCanonicalUrl(service);
  const hasPrice = service.price !== undefined && service.price !== null && service.price > 0;
  const offer: ServiceOfferSchema | undefined = hasPrice
    ? {
        '@type': 'Offer',
        url: canonicalUrl,
        price: String(service.price),
        priceCurrency: 'CLP',
        availability: 'https://schema.org/InStock',
      }
    : undefined;
  return {
    '@type': 'Service',
    name: service.name,
    serviceType: service.name,
    description:
      service.description ||
      `Servicio de ${service.name}${serviceArea() ? ` en ${serviceArea()}` : ''}`,
    url: canonicalUrl,
    provider: { '@id': salonId() },
    ...(serviceArea() ? { areaServed: serviceArea() } : {}),
    duration: isoDuration(durationMinutes(service)),
    ...(offer ? { offers: offer } : {}),
  };
}

/** Grafo combinado nodo del negocio + Service listo para JSON.stringify. */
export function serviceGraph(service: SeoService): SeoGraph {
  return {
    '@context': 'https://schema.org',
    '@graph': [salonSchema(), serviceSchema(service)],
  };
}

const JSONLD_SELECTOR = 'script[data-seo-host="jsonld"]';

export function injectServiceJsonLd(service: SeoService): void {
  let script = document.querySelector<HTMLScriptElement>(JSONLD_SELECTOR);
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-seo-host', 'jsonld');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(serviceGraph(service));
}

export function removeServiceJsonLd(): void {
  document.querySelectorAll<HTMLScriptElement>(JSONLD_SELECTOR).forEach((s) => s.remove());
}
